import * as THREE from 'three';
import { createSeededRandom } from '../../utils/random';

// Procedural hull plating: one panel layout rendered into albedo, roughness and a normal map.
// Shared by every ship; per-ship tint comes from material.color.

const SIZE = 1024;

export type HullTextureSet = { map: THREE.Texture; roughnessMap: THREE.Texture; normalMap: THREE.Texture; emissiveWindows: THREE.Texture };

let cached: HullTextureSet | null = null;

export function hullTextures(renderer?: THREE.WebGLRenderer): HullTextureSet {
  if (cached) return cached;
  const rng = createSeededRandom(1977);
  const albedo = document.createElement('canvas');
  const rough = document.createElement('canvas');
  const height = document.createElement('canvas');
  for (const c of [albedo, rough, height]) c.width = c.height = SIZE;
  const ga = albedo.getContext('2d')!;
  const gr = rough.getContext('2d')!;
  const gh = height.getContext('2d')!;

  ga.fillStyle = 'rgb(190,192,196)';
  ga.fillRect(0, 0, SIZE, SIZE);
  gr.fillStyle = 'rgb(150,150,150)';
  gr.fillRect(0, 0, SIZE, SIZE);
  gh.fillStyle = 'rgb(128,128,128)';
  gh.fillRect(0, 0, SIZE, SIZE);

  // Recursive panel subdivision.
  const panels: [number, number, number, number][] = [];
  const split = (x: number, y: number, w: number, h: number, depth: number) => {
    if (depth > 4 || (depth > 1 && rng() < 0.25) || w < 40 || h < 40) {
      panels.push([x, y, w, h]);
      return;
    }
    if (w > h ? rng() < 0.8 : rng() < 0.2) {
      const s = Math.round(w * (0.3 + rng() * 0.4));
      split(x, y, s, h, depth + 1);
      split(x + s, y, w - s, h, depth + 1);
    } else {
      const s = Math.round(h * (0.3 + rng() * 0.4));
      split(x, y, w, s, depth + 1);
      split(x, y + s, w, h - s, depth + 1);
    }
  };
  const cells = 4;
  for (let i = 0; i < cells; i++) for (let j = 0; j < cells; j++) split((i * SIZE) / cells, (j * SIZE) / cells, SIZE / cells, SIZE / cells, 0);

  for (const [x, y, w, h] of panels) {
    const tone = 176 + Math.floor((rng() - 0.5) * 34);
    const warm = Math.floor((rng() - 0.5) * 6);
    ga.fillStyle = `rgb(${tone + warm},${tone},${tone - warm + 2})`;
    ga.fillRect(x + 1, y + 1, w - 2, h - 2);
    const r = 110 + Math.floor(rng() * 90);
    gr.fillStyle = `rgb(${r},${r},${r})`;
    gr.fillRect(x + 1, y + 1, w - 2, h - 2);
    // Slightly raised or recessed plates
    const lift = rng() < 0.3 ? 118 : rng() < 0.5 ? 138 : 128;
    gh.fillStyle = `rgb(${lift},${lift},${lift})`;
    gh.fillRect(x + 2, y + 2, w - 4, h - 4);
    // Seams
    gh.strokeStyle = 'rgb(60,60,60)';
    gh.lineWidth = 2;
    gh.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    ga.strokeStyle = 'rgba(40,42,46,0.55)';
    ga.lineWidth = 1.5;
    ga.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    // Rivets along some edges
    if (rng() < 0.45) {
      ga.fillStyle = 'rgba(70,72,76,0.6)';
      gh.fillStyle = 'rgb(170,170,170)';
      const step = 10;
      for (let k = x + 6; k < x + w - 6; k += step) {
        ga.fillRect(k, y + 4, 2, 2);
        gh.fillRect(k, y + 4, 2, 2);
        ga.fillRect(k, y + h - 6, 2, 2);
        gh.fillRect(k, y + h - 6, 2, 2);
      }
    }
    // Hatches / vents
    if (w > 70 && h > 50 && rng() < 0.12) {
      const vw = w * 0.5, vh = h * 0.35, vx = x + (w - vw) / 2, vy = y + (h - vh) / 2;
      ga.fillStyle = 'rgba(55,58,62,0.9)';
      ga.fillRect(vx, vy, vw, vh);
      gh.fillStyle = 'rgb(90,90,90)';
      gh.fillRect(vx, vy, vw, vh);
      for (let k = vy + 3; k < vy + vh - 2; k += 5) {
        gh.fillStyle = 'rgb(150,150,150)';
        gh.fillRect(vx + 2, k, vw - 4, 2);
      }
    }
    // Warning chevrons, rare
    if (w > 90 && h > 24 && rng() < 0.05) {
      ga.save();
      ga.beginPath();
      ga.rect(x + 6, y + h - 18, w - 12, 12);
      ga.clip();
      for (let k = x - 20; k < x + w; k += 16) {
        ga.fillStyle = 'rgba(230,180,40,0.85)';
        ga.beginPath();
        ga.moveTo(k, y + h - 6);
        ga.lineTo(k + 8, y + h - 18);
        ga.lineTo(k + 16, y + h - 18);
        ga.lineTo(k + 8, y + h - 6);
        ga.fill();
      }
      ga.restore();
    }
  }
  // Grime: soft streaks.
  for (let i = 0; i < 260; i++) {
    const x = rng() * SIZE, y = rng() * SIZE, len = 20 + rng() * 120;
    const grad = ga.createLinearGradient(x, y, x, y + len);
    grad.addColorStop(0, `rgba(40,38,34,${0.05 + rng() * 0.08})`);
    grad.addColorStop(1, 'rgba(40,38,34,0)');
    ga.fillStyle = grad;
    ga.fillRect(x, y, 2 + rng() * 6, len);
  }

  const map = new THREE.CanvasTexture(albedo);
  map.colorSpace = THREE.SRGBColorSpace;
  const roughnessMap = new THREE.CanvasTexture(rough);
  const normalMap = new THREE.CanvasTexture(heightToNormal(gh.getImageData(0, 0, SIZE, SIZE), 2.2));

  // Window lights sheet: tiny lit rectangles on black.
  const win = document.createElement('canvas');
  win.width = win.height = 256;
  const gw = win.getContext('2d')!;
  gw.fillStyle = '#000';
  gw.fillRect(0, 0, 256, 256);
  for (let y = 8; y < 256; y += 32) {
    for (let x = 4; x < 256; x += 10) {
      if (rng() < 0.55) {
        const c = rng() < 0.8 ? '255,214,160' : '170,220,255';
        gw.fillStyle = `rgba(${c},${0.6 + rng() * 0.4})`;
        gw.fillRect(x, y, 5, 3);
      }
    }
  }
  const emissiveWindows = new THREE.CanvasTexture(win);
  emissiveWindows.colorSpace = THREE.SRGBColorSpace;

  const aniso = renderer?.capabilities.getMaxAnisotropy() ?? 8;
  for (const t of [map, roughnessMap, normalMap, emissiveWindows]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = aniso;
  }
  cached = { map, roughnessMap, normalMap, emissiveWindows };
  return cached;
}

function heightToNormal(img: ImageData, strength: number): HTMLCanvasElement {
  const { width: w, height: h, data } = img;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const g = out.getContext('2d')!;
  const o = g.createImageData(w, h);
  const H = (x: number, y: number) => data[(((y + h) % h) * w + ((x + w) % w)) * 4] / 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * strength;
      const dy = (H(x, y + 1) - H(x, y - 1)) * strength;
      const len = Math.hypot(dx, dy, 1);
      const i = (y * w + x) * 4;
      o.data[i] = ((-dx / len) * 0.5 + 0.5) * 255;
      o.data[i + 1] = ((dy / len) * 0.5 + 0.5) * 255;
      o.data[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
      o.data[i + 3] = 255;
    }
  }
  g.putImageData(o, 0, 0);
  return out;
}
